<template>
  <div class="vehicle-selector-slide-in">
    <button
      :disabled="$attrs.disabled"
      type="button"
      :class="['vehicle-selector-slide-in__trigger', {'vehicle-selector-slide-in__trigger--selections': nodeContext.hasSelections.value}]"
      aria-label="Open vehicle selector slide in"
      @click="setActiveSlideIn(calculatedName)">
      <div class="vehicle-selector-slide-in__trigger-content">
        <template v-if="nodeContext.hasSelections.value">
          <oie-base-icon icon="checkmark-outline" size="m"></oie-base-icon>
          <span v-for="name in nodeContext.selectionNames.value" :key="name" class="caption vehicle-selector-slide-in__pill">{{ name }}</span>
        </template>
        <span v-else>{{ tStatic('selectVehicleLabel') }}</span>
      </div>
      <oie-base-icon icon="chevron-right" size="m"></oie-base-icon>
    </button>
    <oie-portal-wrapper :name="calculatedName">
      <template #title>
        <h2>
          {{ tStatic('selectVehicleSlideInHeader') }}
        </h2>
      </template>
      <div v-if="nodeContext.initialized.value" class="vehicle-selector-slide-in__content">
        <div v-if="showSearch" class="vehicle-selector-slide-in__section">
          <p class="body">
            {{ tStatic('selectVehicleSlideInSearchPreamble') }}
          </p>
          <oie-base-search-select
            :hide-label="true"
            :label="tStatic('selectVehicleSlideInSearchPlaceholder')"
            :placeholder="tStatic('selectVehicleSlideInSearchPlaceholder')"
            :options="nodeContext.searchOptions.value"
            @search="nodeContext.searchNodes"
            @select="nodeContext.handleSelection"
            @close="nodeContext.clearSearchOptions"></oie-base-search-select>
        </div>
        <div class="vehicle-selector-slide-in__section">
          <p class="body">
            {{ showSearch ? tStatic('selectVehicleSlideInOrSelectText') : tStatic('selectVehicleSlideInSelectByStepsText') }}
          </p>
          <oie-vehicle-selector-accordion-section
            v-for="(val, key) in nodeContext.nodeData.value"
            :key="val.node.code"
            :level="key"
            :context="nodeContext"
            :show-add-node="enableAddNode"
            @select="nodeContext.addLevel($event, key)">
          </oie-vehicle-selector-accordion-section>
          <template v-if="enableAddNode && nodeContext.endOfNodeHierarchy.value">
            <p v-html="noMoreNodes()"></p>
            <oie-vehicle-selector-add-node
              :toggle-text="tStatic('selectVehicleSlideInAddNodesToNode').replace('{0}', nodeContext.deepestNodeSelection.value.name)"
              :context="nodeContext"
              :level="nodeContext.lastLevel.value"></oie-vehicle-selector-add-node>
          </template>
        </div>
        <oie-action-wrapper>
          <oie-base-action size="large" theme="skeleton" @click="setActiveSlideIn(null)">
            {{ tStatic('close') }}
          </oie-base-action>
          <oie-base-action v-if="showConfirm" size="large" @click="handleConfirm">
            {{ tStatic('confirm') }}
          </oie-base-action>
        </oie-action-wrapper>
      </div>
    </oie-portal-wrapper>
  </div>
</template>

<script>
import { defineComponent, onMounted, watch, computed } from '@vue/composition-api'
import _ from 'lodash'
import { mapActions } from 'vuex'

export default defineComponent({
  methods: {
    ...mapActions('ui', ['setActiveSlideIn'])
  },
  props: {
    name: {
      type: String,
      default: ''
    },
    source: {
      type: Function,
      required: true
    },
    forceModelSelection: {
      type: Boolean,
      default: false
    },
    enableAddNode: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { root, emit }) {
    const calculatedName = props.name || _.uniqueId('vehicle-selector-slide-in')
    const nodeContext = props.source()

    onMounted(async () => {
      await nodeContext.initializeNodeData()
      emit('initialized', nodeContext.deepestNodeSelection.value)
      root.subscribe(`clear-${calculatedName}`, () => {
        nodeContext.resetLowerLevels(0)
        nodeContext.saveSelectedNodeData()
      })
    })

    const showConfirm = computed(() => {
      return nodeContext.hasSelections.value && (props.forceModelSelection ? nodeContext.isModelSelected.value : true)
    })

    const showSearch = computed(() => {
      return !!nodeContext.searchNodes
    })

    watch(() => root.$store.getters['ui/activeSlideIn'], (newVal) => {
      if (!(newVal === calculatedName)) {
        nodeContext.resetNodeData()
      }
    })

    const handleConfirm = async () => {
      nodeContext.saveSelectedNodeData()
      emit('confirm', nodeContext.deepestNodeSelection.value)
      root.$store.dispatch('ui/setActiveSlideIn', null)
    }

    const noMoreNodes = () => {
      const text = root.tStatic('selectVehicleSlideInNoMoreNodesUnderCurrent') || 'No more nodes available under {0}. You can add your own nodes by clicking the button below.'
      return text.replace('{0}', `<strong>${nodeContext.deepestNodeSelection.value.name}</strong>`)
    }

    return {
      nodeContext,
      calculatedName,
      handleConfirm,
      showConfirm,
      showSearch,
      noMoreNodes
    }
  }
})
</script>

<style lang="scss">
.vehicle-selector-slide-in__trigger {
  @include reset-button;
  border-radius: 2px;
  padding: measure(quarter);
  background-color: color(background-blue-7);
  border: 1px solid color(brand-blue-3);
}

.vehicle-selector-slide-in__trigger:disabled {
  opacity: 0.5;
}

.vehicle-selector-slide-in__trigger:hover:not(:disabled) {
  background-color: color(background-blue-4);
}

.vehicle-selector-slide-in__trigger--selections {
  background-color: color(background-light-green);
  border: 1px solid color(border-green-3);
}

.vehicle-selector-slide-in__trigger-content {
  display: flex;
  align-items: center;
  gap: measure(eighth);
  flex-wrap: wrap;
}

.vehicle-selector-slide-in__content {
  margin-top: 40px;

  .action-wrapper {
    justify-content: flex-start;
  }
}

.vehicle-selector-slide-in__content >*:not(:last-child) {
  margin-bottom: measure(full);
}

.vehicle-selector-slide-in__section >*:not(:last-child) {
  margin-bottom: measure(half);
}

.vehicle-selector-slide-in__pill {
  border: 1px solid color(black);
  padding: 3px 5px;
  border-radius: 100px;
}
</style>
